import type { Question, Offer } from '@/types/config'

interface VariantData {
  id: string
  name: string
  text?: string
  description?: string
  answers?: Record<string, string | { label: string; imageUrl?: string }>
  parts?: {
    title?: string
    description?: string
    buttonText?: string
    imageUrl?: string
  }
  [key: string]: any
}

interface VariantDiff {
  hasQuestionTextChanged: boolean
  hasDescriptionChanged?: boolean
  changedAnswers: Set<string> // Set of segment IDs that have changed
  changedAnswerLabels: Set<string> // Segment IDs where label changed
  changedAnswerImages: Set<string> // Segment IDs where image changed
  parts?: {
    title?: boolean
    description?: boolean
    buttonText?: boolean
    imageUrl?: boolean
  }
}

/**
 * Compares a question (control) with a variant to determine what fields have changed
 * @param question The control question object
 * @param variant The variant data to compare against
 * @param segmentGroup Optional segment group for getting original segment names
 * @returns An object describing what has changed
 */
export function getVariantDiff(question: Question, variant: VariantData, segmentGroup?: any): VariantDiff {
  const diff: VariantDiff = {
    hasQuestionTextChanged: false,
    hasDescriptionChanged: false,
    changedAnswers: new Set(),
    changedAnswerLabels: new Set(),
    changedAnswerImages: new Set()
  }

  // Check if question text has changed
  const variantText = variant.text || ''
  const controlText = question.text || ''
  if (variantText !== controlText) {
    diff.hasQuestionTextChanged = true
  }

  // Check if description has changed
  const variantDescription = variant.description || ''
  const controlDescription = question.description || ''
  if (variantDescription !== controlDescription) {
    diff.hasDescriptionChanged = true
  }

  // Check answer changes
  if (variant.answers) {
    // If we have a segmentGroup, use it to get the original segment names
    if (segmentGroup && segmentGroup.segments) {
      segmentGroup.segments.forEach((segment: any) => {
        const segmentId = segment.id
        const variantAnswer = variant.answers![segmentId]
        
        if (!variantAnswer) return

        // Get control values from the segment
        const controlLabel = segment.name || ''
        
        // Handle both string (legacy) and object format for variant answers
        let variantLabel = ''
        
        if (typeof variantAnswer === 'string') {
          variantLabel = variantAnswer
        } else if (variantAnswer && typeof variantAnswer === 'object') {
          variantLabel = variantAnswer.label || ''
        }

        // Compare labels
        if (variantLabel !== controlLabel) {
          diff.changedAnswers.add(segmentId)
          diff.changedAnswerLabels.add(segmentId)
        }
      })
    } else if (question.options) {
      // Fallback to using question options if no segment group
      question.options.forEach(option => {
        const segmentId = option.segmentId
        const variantAnswer = variant.answers![segmentId]
        
        if (!variantAnswer) return

        // Get control values
        const controlLabel = option.label || ''
        const controlImageUrl = option.imageUrl || ''

        // Handle both string (legacy) and object format for variant answers
        let variantLabel = ''
        let variantImageUrl = ''
        
        if (typeof variantAnswer === 'string') {
          variantLabel = variantAnswer
        } else if (variantAnswer && typeof variantAnswer === 'object') {
          variantLabel = variantAnswer.label || ''
          variantImageUrl = variantAnswer.imageUrl || ''
        }

        // Compare labels
        if (variantLabel !== controlLabel) {
          diff.changedAnswers.add(segmentId)
          diff.changedAnswerLabels.add(segmentId)
        }

        // Compare images
        if (variantImageUrl !== controlImageUrl) {
          diff.changedAnswers.add(segmentId)
          diff.changedAnswerImages.add(segmentId)
        }
      })
    }
  }

  return diff
}

/**
 * Helper to check if a specific answer has changed in a variant
 * @param segmentId The segment ID to check
 * @param diff The diff object from getVariantDiff
 * @returns true if the answer has any changes
 */
export function hasAnswerChanged(segmentId: string, diff: VariantDiff): boolean {
  return diff.changedAnswers.has(segmentId)
}

/**
 * Helper to check if a specific answer's label has changed
 * @param segmentId The segment ID to check
 * @param diff The diff object from getVariantDiff
 * @returns true if the label has changed
 */
export function hasAnswerLabelChanged(segmentId: string, diff: VariantDiff): boolean {
  return diff.changedAnswerLabels.has(segmentId)
}

/**
 * Helper to check if a specific answer's image has changed
 * @param segmentId The segment ID to check
 * @param diff The diff object from getVariantDiff
 * @returns true if the image has changed
 */
export function hasAnswerImageChanged(segmentId: string, diff: VariantDiff): boolean {
  return diff.changedAnswerImages.has(segmentId)
}

/**
 * Get CSS classes for styling changed elements
 * @param hasChanged Whether the element has changed
 * @returns CSS class string
 */
export function getVariantChangeClasses(hasChanged: boolean): string {
  if (!hasChanged) return ''
  
  // Return classes that add visual distinction for changed elements
  return 'variant-changed'
}

/**
 * Get inline styles for changed elements
 * @param hasChanged Whether the element has changed
 * @returns Style object
 */
export function getVariantChangeStyles(hasChanged: boolean): Record<string, string> {
  if (!hasChanged) return {}
  
  return {
    textDecoration: 'underline',
    textDecorationColor: '#f59e0b', // Amber color
    textDecorationStyle: 'solid',
    textDecorationThickness: '2px',
    textUnderlineOffset: '2px'
  }
}

/**
 * Compares an offer (control) with a variant to determine what fields have changed
 * @param offer The control offer object
 * @param variant The variant data to compare against
 * @returns An object describing what has changed
 */
export function getOfferVariantDiff(offer: Offer, variant: VariantData): VariantDiff {
  const diff: VariantDiff = {
    hasQuestionTextChanged: false,
    changedAnswers: new Set(),
    changedAnswerLabels: new Set(),
    changedAnswerImages: new Set(),
    parts: {
      title: false,
      description: false,
      buttonText: false,
      imageUrl: false
    }
  }

  // Check offer parts changes
  if (variant.parts && offer.parts) {
    diff.parts!.title = (variant.parts.title || '') !== (offer.parts.title || '')
    diff.parts!.description = (variant.parts.description || '') !== (offer.parts.description || '')
    diff.parts!.buttonText = (variant.parts.buttonText || '') !== (offer.parts.buttonText || '')
    diff.parts!.imageUrl = (variant.parts.imageUrl || '') !== (offer.parts.imageUrl || '')
  }

  return diff
}